import React, { useState } from 'react';
import { Star, Loader2, Search, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from 'sonner';

export const AdminSpotlightTab = ({ api }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [note, setNote] = useState('');
  const [weekStart, setWeekStart] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [spotlights, setSpotlights] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchSpotlights = async () => {
    try {
      const res = await api.get('/spotlight?limit=10');
      setSpotlights(res.data);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to fetch spotlights:', error);
    }
  };

  if (!loaded) fetchSpotlights();

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/stories?search=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Story search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedStory) { toast.error('Please select a story first.'); return; }
    setSubmitting(true);
    try {
      await api.post('/spotlight', {
        story_id: selectedStory.id,
        selected_by: '',
        week_start: weekStart,
        note: note
      });
      toast.success(`"${selectedStory.title}" added to Spotlight!`);
      setSelectedStory(null);
      setNote('');
      setSearchQuery('');
      setSearchResults([]);
      fetchSpotlights();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add spotlight.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Spotlight Form */}
      <Card className="border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-brand-accent" />
            Add Spotlight Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Story Search */}
          <div className="space-y-2">
            <Label>Search for a story</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
                data-testid="spotlight-search"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />}
            </div>
            {searchResults.length > 0 && !selectedStory && (
              <div className="border border-brand-primary/10 rounded-lg max-h-48 overflow-y-auto">
                {searchResults.map(story => (
                  <button
                    key={story.id}
                    onClick={() => { setSelectedStory(story); setSearchResults([]); setSearchQuery(story.title); }}
                    className="w-full text-left px-4 py-3 hover:bg-surface-alt transition-colors border-b border-brand-primary/5 last:border-0"
                    data-testid={`spotlight-story-${story.id}`}
                  >
                    <p className="font-medium text-text-primary text-sm">{story.title}</p>
                    <p className="text-xs text-text-muted">by {story.author_name} &middot; {story.genre}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Story */}
          {selectedStory && (
            <div className="flex items-center justify-between p-3 bg-brand-accent/10 rounded-lg">
              <div>
                <p className="font-medium text-text-primary text-sm">{selectedStory.title}</p>
                <p className="text-xs text-text-muted">by {selectedStory.author_name}</p>
              </div>
              <button onClick={() => { setSelectedStory(null); setSearchQuery(''); }} className="text-red-400 hover:text-red-600 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Week Start */}
          <div className="space-y-2">
            <Label>Week of</Label>
            <Input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              data-testid="spotlight-week"
            />
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="Why this piece stands out..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              data-testid="spotlight-note"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedStory}
            className="btn-accent gap-2 w-full sm:w-auto"
            data-testid="spotlight-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
            Add to Spotlight
          </Button>
        </CardContent>
      </Card>

      {/* Existing Spotlights */}
      {spotlights.length > 0 && (
        <Card className="border-brand-primary/10">
          <CardHeader>
            <CardTitle className="text-base">Current Spotlights ({spotlights.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {spotlights.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-surface-alt rounded-lg">
                  <div>
                    <p className="font-medium text-text-primary text-sm">{s.story_title}</p>
                    <p className="text-xs text-text-muted">by {s.story_author_name} &middot; Week of {new Date(s.week_start).toLocaleDateString()}</p>
                    {s.note && <p className="text-xs text-text-secondary mt-1 italic">"{s.note}"</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
